import { ClaimWithCheckoutRelationsDTO, OrderWithOrderCustomersDTO } from '@reposit/api-client';
import { CLAIM_PROPOSALS_FILTER } from '../../../components/ClaimPayments/constants';
import { ClaimPaymentOrderFilterOption } from '../../../components/ClaimPayments/order-filter';
import { isPaymentPlanInterestCollectible } from '../../../utils/payment-plan.utils';

const ARBITRATION_ADMIN_FEE_LABEL = 'Arbitration admin fee';
const productLabels: { [productId: string]: string } = {
  REPOSIT: 'Reposit',
  CLAIM: 'Claim',
  CLAIM_PROPOSAL: 'Claim proposal',
  ARBITRATION: 'Arbitration',
  TOP_UP: 'Top up',
  ARBITRATION_ADMIN_FEE: ARBITRATION_ADMIN_FEE_LABEL,
};

const getProductLabel = (order: OrderWithOrderCustomersDTO | undefined, fallbackLabel: string) => {
  if (!order || !order.productId) {
    return fallbackLabel;
  }

  return productLabels[order.productId] || order.productId;
};

const getClaimProposalOrderSummaryLabel = (proposal: NonNullable<ClaimWithCheckoutRelationsDTO['proposals']>[number]) => {
  const productLabel = getProductLabel(proposal.order, 'Claim proposal');
  if (proposal.order && proposal.order.productId === 'CLAIM_PROPOSAL') {
    const roundLabel = proposal.round ? ` round ${proposal.round}` : '';
    return `${productLabel}${roundLabel}`;
  }

  return productLabel;
};

const getPaymentPlanInterestState = (order: OrderWithOrderCustomersDTO): boolean | undefined => {
  const paymentPlans = (order.orderCustomers || [])
    .map((orderCustomer) => orderCustomer.paymentPlan)
    .filter((paymentPlan) => !!paymentPlan);

  return paymentPlans.length ? paymentPlans.some(isPaymentPlanInterestCollectible) : undefined;
};

const addOrderFilterOption = (
  options: ClaimPaymentOrderFilterOption[],
  seenOrderIds: { [orderId: string]: boolean },
  order: OrderWithOrderCustomersDTO | undefined,
  label: string,
) => {
  if (!order || !order.id || seenOrderIds[order.id]) {
    return;
  }

  seenOrderIds[order.id] = true;
  const hasCollectiblePaymentPlanInterest = getPaymentPlanInterestState(order);
  options.push({
    value: order.id,
    label,
    orderIds: [order.id],
    ...(order.amount !== undefined ? { amount: order.amount } : {}),
    ...(order.productId !== undefined ? { productId: order.productId } : {}),
    ...(hasCollectiblePaymentPlanInterest !== undefined ? { hasCollectiblePaymentPlanInterest } : {}),
    ...(order.refundHandling !== undefined ? { refundHandling: order.refundHandling } : {}),
    ...(order.disputeHandling !== undefined ? { disputeHandling: order.disputeHandling } : {}),
  });
};

export const getClaimPaymentOrderFilterOptions = (
  claim: ClaimWithCheckoutRelationsDTO | undefined,
  activeOrderId?: string,
): ClaimPaymentOrderFilterOption[] => {
  const options: ClaimPaymentOrderFilterOption[] = [];
  const seenOrderIds: { [orderId: string]: boolean } = {};

  if (!claim) {
    return options;
  }

  addOrderFilterOption(
    options,
    seenOrderIds,
    claim.adjudicationFeeOrder,
    getProductLabel(claim.adjudicationFeeOrder, ARBITRATION_ADMIN_FEE_LABEL),
  );
  addOrderFilterOption(
    options,
    seenOrderIds,
    claim.arbitration ? claim.arbitration.repaymentOrder : undefined,
    getProductLabel(claim.arbitration ? claim.arbitration.repaymentOrder : undefined, 'Arbitration repayment order'),
  );

  const seenClaimProposalOrderIds: { [orderId: string]: boolean } = {};
  const claimProposalProductOrders = (claim.proposals || [])
    .filter((proposal) => proposal.order && proposal.order.productId === 'CLAIM_PROPOSAL')
    .map((proposal) => proposal.order)
    .filter((order): order is OrderWithOrderCustomersDTO => {
      if (!order || seenClaimProposalOrderIds[order.id]) {
        return false;
      }

      seenClaimProposalOrderIds[order.id] = true;
      return true;
    });
  const claimProposalProductOrderIds = claimProposalProductOrders.map((order) => order.id);

  if (claimProposalProductOrderIds.length > 0) {
    options.push({
      value: CLAIM_PROPOSALS_FILTER,
      label: 'Claim proposal',
      orderIds: claimProposalProductOrderIds,
    });
  }

  (claim.proposals || [])
    .filter((proposal) => proposal.order && proposal.order.productId !== 'CLAIM_PROPOSAL')
    .forEach((proposal) => {
      addOrderFilterOption(options, seenOrderIds, proposal.order, getClaimProposalOrderSummaryLabel(proposal));
    });

  const activeOrder = activeOrderId ? options.find((option) => option.orderIds.includes(activeOrderId)) : undefined;
  const inactiveOrders = options.filter((option) => !(activeOrderId && option.orderIds.includes(activeOrderId)));

  return activeOrder ? [activeOrder, ...inactiveOrders] : options;
};

export const getClaimPaymentOrderSummaryFilterOptions = (
  claim: ClaimWithCheckoutRelationsDTO | undefined,
  activeOrderId?: string,
): ClaimPaymentOrderFilterOption[] => {
  const options: ClaimPaymentOrderFilterOption[] = [];
  const seenOrderIds: { [orderId: string]: boolean } = {};

  if (!claim) {
    return options;
  }

  addOrderFilterOption(
    options,
    seenOrderIds,
    claim.adjudicationFeeOrder,
    getProductLabel(claim.adjudicationFeeOrder, ARBITRATION_ADMIN_FEE_LABEL),
  );

  addOrderFilterOption(
    options,
    seenOrderIds,
    claim.arbitration ? claim.arbitration.repaymentOrder : undefined,
    getProductLabel(claim.arbitration ? claim.arbitration.repaymentOrder : undefined, 'Arbitration repayment order'),
  );

  (claim.proposals || []).forEach((proposal) => {
    addOrderFilterOption(options, seenOrderIds, proposal.order, getClaimProposalOrderSummaryLabel(proposal));
  });

  const activeOrder = activeOrderId ? options.find((option) => option.orderIds.includes(activeOrderId)) : undefined;
  const inactiveOrders = options.filter((option) => !(activeOrderId && option.orderIds.includes(activeOrderId)));

  return activeOrder ? [activeOrder, ...inactiveOrders] : options;
};
